import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { components } from '@awj/api-client';

import { fetchAllPages } from './export.ts';
import { admin } from './queries.ts';

type SaveProduct = components['schemas']['SaveProductRequest'];
type SaveCategory = components['schemas']['SaveCategoryRequest'];

export interface ProductQuery {
  search?: string;
  category?: string;
  status?: string;
  sort?: string;
  page?: number;
  pageSize?: number;
}

/** The filters as the API takes them — shared by the list and its export, so the two agree. */
function filters(query: ProductQuery) {
  return {
    search: query.search || undefined,
    category: query.category || undefined,
    status: query.status || undefined,
    sort: query.sort || undefined,
  };
}

export function useProducts(query: ProductQuery) {
  return useQuery({
    // The query object is part of the key, so every filter combination caches separately and
    // going back to a previous filter is instant rather than another round trip.
    queryKey: ['products', 'list', query],
    queryFn: () =>
      admin().products({ ...filters(query), page: query.page ?? 1, pageSize: query.pageSize ?? 20 }),
  });
}

/** Every product the filters match, in the chosen order, for an export. */
export function allProducts(query: ProductQuery) {
  return fetchAllPages((page, pageSize) => admin().products({ ...filters(query), page, pageSize }));
}

export function useProduct(id: number | null) {
  return useQuery({
    queryKey: ['products', 'one', id],
    queryFn: () => admin().product(id!),
    // `null` is "creating a new one", which has nothing to fetch.
    enabled: id !== null,
  });
}

export function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: () => admin().categories(),
  });
}

/**
 * Invalidation, in one place.
 *
 * Saving a product moves the dashboard's product count and its low-stock tile as surely as it
 * moves the list, so both sections are dropped. Missing one is how a member of staff edits
 * stock, returns to the dashboard, and is told the old number.
 */
function useCatalogInvalidation() {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: ['products'] });
    void queryClient.invalidateQueries({ queryKey: ['categories'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };
}

export function useSaveProduct() {
  const invalidate = useCatalogInvalidation();

  return useMutation({
    mutationFn: ({ id, body }: { id: number | null; body: SaveProduct }) =>
      id === null ? admin().createProduct(body) : admin().updateProduct(id, body),
    onSuccess: invalidate,
  });
}

export function useArchiveProduct() {
  const invalidate = useCatalogInvalidation();

  return useMutation({
    mutationFn: ({ id, archived }: { id: number; archived: boolean }) =>
      archived ? admin().restoreProduct(id) : admin().archiveProduct(id),
    onSuccess: invalidate,
  });
}

export function useDeleteProduct() {
  const invalidate = useCatalogInvalidation();

  return useMutation({
    mutationFn: (id: number) => admin().deleteProduct(id),
    onSuccess: invalidate,
  });
}

export function useSaveCategory() {
  const invalidate = useCatalogInvalidation();

  return useMutation({
    mutationFn: ({ id, body }: { id: number | null; body: SaveCategory }) =>
      id === null ? admin().createCategory(body) : admin().updateCategory(id, body),
    onSuccess: invalidate,
  });
}

export function useDeleteCategory() {
  const invalidate = useCatalogInvalidation();

  return useMutation({
    mutationFn: (id: number) => admin().deleteCategory(id),
    onSuccess: invalidate,
  });
}

export function useProductImages(productId: number | null) {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['products', 'one', productId] });
    void queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
  };

  return {
    upload: useMutation({
      mutationFn: (file: File) => admin().uploadProductImage(productId!, file),
      onSuccess: refresh,
    }),
    updateAlt: useMutation({
      mutationFn: ({ imageId, altAr, altEn }: { imageId: number; altAr: string; altEn: string }) =>
        admin().updateProductImage(productId!, imageId, { altAr, altEn }),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: (imageId: number) => admin().deleteProductImage(productId!, imageId),
      onSuccess: refresh,
    }),
    reorder: useMutation({
      mutationFn: (orderedIds: number[]) => admin().reorderProductImages(productId!, orderedIds),
      onSuccess: refresh,
    }),
  };
}
